<template>
  <div class="grid md:flex md:items-center md:justify-between">
    <div class="grid gap-4 md:flex md:items-center md:justify-start">
      <SearchUserInput :allowed-roles="[UserRole.CUSTOMER]" @update-user="handleUpdateUser" />
      <CVButton
        variant="default"
        :disabled="!selectedCustomerForSwap"
        :is-loading="isSaving"
        @click.prevent="swapToSelectedCustomer"
        >{{ swapButtonText ?? t('swap') }}</CVButton
      >
      <CVButton variant="link" :is-loading="isSaving" @click.prevent="createNewCustomer">{{
        t('createNewCustomer')
      }}</CVButton>
    </div>
    <CVButton variant="secondary" @click.prevent="$emit('cancel')">{{ t('cancel') }}</CVButton>
  </div>

  <CreateCustomerModal
    v-model:show-modal="showCreateCustomerModal"
    :schema="schema"
    :initial-customer-data="initialCustomerData"
    :is-saving="isSaving"
    @new-customer-created="$emit('swapCustomer', $event)"
    @on-finished="$emit('cancel')"
    @on-cancel="showCreateCustomerModal = false"
  />
</template>

<script lang="ts" setup>
import { ref } from 'vue';
import CreateCustomerModal from './CreateCustomerModal.vue';
import SearchUserInput from './SearchUserInput.vue';
import { UserRole } from '@/entities/auth/user-role.enum';
import type { CreateUserDto, User } from '@/entities/auth/user.entity';
import type { ObjectSchema } from 'yup';
import { useI18n } from 'vue-i18n';
import CVButton from '@/components/buttons/CVButton.vue';

defineProps<{
  schema: ObjectSchema<any>;
  initialCustomerData?: Partial<CreateUserDto>;
  swapButtonText?: string;
  isSaving?: boolean;
}>();

const emit = defineEmits<{
  (e: 'cancel'): void;
  (e: 'swapCustomer', customer: User): void;
}>();

const { t } = useI18n();

const selectedCustomerForSwap = ref<User>();
const showCreateCustomerModal = ref(false);

const handleUpdateUser = (user: User | undefined) => {
  selectedCustomerForSwap.value = user;
};

const swapToSelectedCustomer = () => {
  if (!selectedCustomerForSwap.value) return;
  emit('swapCustomer', selectedCustomerForSwap.value);
};

const createNewCustomer = () => {
  showCreateCustomerModal.value = true;
};
</script>

<i18n lang="json">
{
  "en": {
    "swap": "Swap",
    "createNewCustomer": "+ Create new customer"
  },
  "de": {
    "swap": "Austauschen",
    "createNewCustomer": "+ Neuen Kunden anlegen"
  },
  "es": {
    "swap": "Cambiar",
    "createNewCustomer": "+ Crear cliente nuevo"
  }
}
</i18n>
